import React, { useCallback, useEffect, useState } from 'react';

import AssetUploader from '../../VideoEditor/AssetUploader';
import apiClient from '../../../services/api';
import { ProjectAsset, VideoProject } from '../../../types/editor';

interface UploadStepProps {
  projectId: number;
  /** Called whenever the user-controlled ordered list changes (load,
   * upload, remove, reorder). Parent uses this to decide whether
   * "Next" is enabled and to know what order to build the timeline
   * in when the user advances. */
  onAssetsChanged: (assets: ProjectAsset[]) => void;
  /** Project assets to keep out of the tile list entirely — the
   * library-audio copy lives in the Music field instead, so showing
   * it here would double-represent (and double-attach) it. */
  hiddenAssetIds?: number[];
}

/* User-controlled upload list. The wizard's "build the timeline from
 * these in this order" decision is deferred to the Next handler in
 * the parent so the user can drag tiles around freely without
 * incurring a PATCH per move.
 *
 * Reordering UX: native HTML5 drag-and-drop on desktop, ↑/↓ buttons
 * on every tile so touch users (and screen-reader users) have a
 * non-drag path too. */
export default function UploadStep({ projectId, onAssetsChanged, hiddenAssetIds }: UploadStepProps) {
  const [assets, setAssets] = useState<ProjectAsset[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  // Joined string so the effect dep is a stable primitive.
  const hiddenKey = (hiddenAssetIds || []).join(',');

  // Load assets on mount so users coming back via the back button see
  // what they already uploaded. Order comes from the server's
  // ProjectAsset.created_at — the first upload appears first.
  useEffect(() => {
    const hidden = new Set(hiddenKey ? hiddenKey.split(',').map(Number) : []);
    apiClient.get<VideoProject>(`/editor/projects/${projectId}/`)
      .then((p) => {
        const initial = (p.assets || []).filter((a) => !hidden.has(a.id));
        setAssets(initial);
        onAssetsChanged(initial);
      })
      .catch((e: Error) => setLoadError(e.message || 'Failed to load project'));
  }, [projectId, onAssetsChanged, hiddenKey]);

  const handleUploaded = useCallback((asset: ProjectAsset) => {
    setAssets((prev) => {
      // Append the new asset at the end of whatever the user has
      // already arranged. Less surprising than splicing it into the
      // middle of their carefully-chosen sequence.
      const next = [...prev, asset];
      onAssetsChanged(next);
      return next;
    });
  }, [onAssetsChanged]);

  const handleRemove = useCallback(async (assetId: number) => {
    if (!window.confirm('Remove this upload?')) return;
    try {
      await apiClient.delete(`/editor/projects/${projectId}/assets/${assetId}/`);
      setAssets((prev) => {
        const next = prev.filter((a) => a.id !== assetId);
        onAssetsChanged(next);
        return next;
      });
    } catch (e) {
      window.alert((e as Error).message || 'Remove failed');
    }
  }, [projectId, onAssetsChanged]);

  function move(fromIdx: number, toIdx: number): void {
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return;
    setAssets((prev) => {
      if (toIdx >= prev.length) return prev;
      const next = prev.slice();
      const [picked] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, picked);
      onAssetsChanged(next);
      return next;
    });
  }

  return (
    <div className="wizard-upload">
      <AssetUploader projectId={projectId} onUploaded={handleUploaded} />
      {loadError && <div className="banner banner--error">{loadError}</div>}

      {assets.length > 0 && (
        <section className="wizard-upload-list">
          <ul
            className="wizard-upload-tiles"
            onDragOver={(e) => e.preventDefault()}
          >
            {assets.map((a, i) => {
              const isDragging = dragIndex === i;
              const isOver = overIndex === i && dragIndex !== null && dragIndex !== i;
              return (
                <li
                  key={a.id}
                  className={[
                    'wizard-upload-tile',
                    `kind-${a.kind}`,
                    isDragging ? 'is-dragging' : '',
                    isOver ? 'is-drop-target' : '',
                  ].filter(Boolean).join(' ')}
                  draggable
                  onDragStart={(e) => {
                    setDragIndex(i);
                    e.dataTransfer.effectAllowed = 'move';
                    // Firefox needs something in dataTransfer to fire drag events.
                    e.dataTransfer.setData('text/plain', String(a.id));
                  }}
                  onDragEnter={() => setOverIndex(i)}
                  onDragOver={(e) => { e.preventDefault(); setOverIndex(i); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragIndex !== null) move(dragIndex, i);
                    setDragIndex(null);
                    setOverIndex(null);
                  }}
                  onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                >
                  <div className="wizard-upload-order" aria-hidden>{i + 1}</div>
                  <div className="wizard-upload-thumb">
                    {a.kind === 'image' && a.file_url && (
                      <img alt={a.original_filename} src={a.file_url} />
                    )}
                    {a.kind === 'video' && a.file_url && (
                      <video src={a.file_url} muted preload="metadata" playsInline />
                    )}
                    {a.kind === 'audio' && (
                      <div className="wizard-upload-audio">audio</div>
                    )}
                    <span className="wizard-upload-kind">{a.kind}</span>
                  </div>
                  <div className="wizard-upload-meta">
                    <span className="wizard-upload-name" title={a.original_filename}>
                      {a.original_filename || `asset #${a.id}`}
                    </span>
                  </div>
                  <div className="wizard-upload-actions">
                    <button
                      type="button"
                      className="ghost btn--icon-sm"
                      onClick={() => move(i, i - 1)}
                      disabled={i === 0}
                      aria-label="Move up"
                      title="Move up"
                    >↑</button>
                    <button
                      type="button"
                      className="ghost btn--icon-sm"
                      onClick={() => move(i, i + 1)}
                      disabled={i === assets.length - 1}
                      aria-label="Move down"
                      title="Move down"
                    >↓</button>
                    <button
                      type="button"
                      className="ghost btn--sm"
                      onClick={() => handleRemove(a.id)}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
